import { DateHelper } from "@eljouren/utils";
import { eachDayOfInterval } from "date-fns";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { useApiClients } from "../../../../hooks/use-api-clients";
import { useBookingSuggestionsForm } from "./use-booking-suggestions-form";

export function useHandymanQualifications() {
  const { handymanRepo } = useApiClients();

  const qualificationsRes = useQuery("handyman-qualifications", async () => {
    return handymanRepo.fetchAllHandymanQualifications();
  });

  const qualifications = qualificationsRes.data ?? [];

  return qualifications;
}

type Qualification = ReturnType<typeof useHandymanQualifications>[number];

type DatesInput = {
  form: ReturnType<typeof useBookingSuggestionsForm>;
  selectedQualification: Qualification | undefined;
  selectedWeekNumber: number | undefined;
};

type WeekObject = {
  start: Date;
  end: Date;
  number: number;
  label: string;
};

type DayObject = {
  date: Date;
  selectable: boolean;
  dayOfWeek: string;
};

export type UseBookingSuggestionDatesOutput = {
  selectableWeeks: WeekObject[];
  selectedWeek: WeekObject;
  days: DayObject[];
};

export function useBookingSuggestionDates(
  input: DatesInput
): UseBookingSuggestionDatesOutput {
  const isOnDuty = !!input.selectedQualification?.onDuty;

  const helper = new DateHelper();
  const currentWeekNumber = helper.week;

  const selectableWeeks = useMemo<WeekObject[]>(
    () => {
      const nowHelper = new DateHelper();
      const endOfCurrentWeek = nowHelper.endOfWeek;
      const startOfToday = nowHelper.startOfDay;
      /* 
		  Current week and the 5 following
	  */
      const weeks: WeekObject[] = Array.from({ length: 6 }, (_, i) => {
        const helper = new DateHelper().add("weeks", i);
        const start = helper.startOfWeek.date;
        const end = helper.endOfWeek.date;
        const number = helper.week;
        const label = `Vecka ${number}`;
        return {
          start,
          end,
          number,
          label,
        };
      });

      if (isOnDuty) {
        /* 
			Allow choosing the current date and weekdays when the type
			of work is "on duty work".
		*/
        return weeks;
      }

      /* 
		  If it's not "on duty work", skip the first week if it only includes
		  weekends.
	  */
      const daysLeftInCurrentWeek = eachDayOfInterval({
        start: startOfToday.date,
        end: endOfCurrentWeek.date,
      });

      const onlyWeekends = daysLeftInCurrentWeek.every(
        (d) => new DateHelper(d).isWeekend
      );
      if (onlyWeekends) {
        /* 
		 Remove the current week from the selectable options
	   */
        return weeks.slice(1);
      }

      /* 
		Current week has at least one weekday left. Return all weeks.
	*/

      return weeks;
    },
    // eslint-disable-next-line
    [currentWeekNumber, isOnDuty]
  );

  const selectedWeek = useMemo(() => {
    return (
      selectableWeeks.find((val) => val.number === input.selectedWeekNumber) ??
      selectableWeeks[0]
    );
  }, [input.selectedWeekNumber, selectableWeeks]);

  const days = useMemo<DayObject[]>(() => {
    const nowHelper = new DateHelper();
    const startOfToday = nowHelper.startOfDay;

    const daysInSelectedWeek = eachDayOfInterval({
      start: selectedWeek.start,
      end: selectedWeek.end,
    });

    if (selectedWeek.number !== currentWeekNumber) {
      /* 
			You can't select previous weeks, so if this is true we know that the selected
			week is a week in the future. All days are therefor selectable.
		*/
      return daysInSelectedWeek.map((date) => {
        const helper = new DateHelper(date);
        return {
          date,
          selectable: true,
          dayOfWeek: helper.dayOfWeek,
        };
      });
    }

    return daysInSelectedWeek.map((date) => {
      const helper = new DateHelper(date);

      return {
        date,
        selectable: !helper.isBefore(startOfToday.date),
        dayOfWeek: helper.dayOfWeek,
      };
    });
  }, [selectedWeek, currentWeekNumber]);

  return {
    selectedWeek,

    selectableWeeks,
    days,
  };
}
